/**
 * Frontend re-aggregation for the Macro Score panel.
 *
 * The backend (stock_analyzer/services/macro/macro_score.py) does all the hard
 * NORMALIZATION — turning each raw signal into a 0-100 sub-score — and ships the
 * per-signal sub-scores + default weights in the dashboard's `score` block. This
 * module only re-runs the cheap WEIGHTED-MEAN AGGREGATION so the sliders update
 * instantly with no API round-trip.
 *
 * `aggregate()` here is a line-for-line port of the Python `aggregate()` (and of
 * scoring.js `aggregateScore`): null sub-scores and non-positive weights are
 * excluded, surviving weights auto-renormalize, and the result is null when
 * nothing survives. macroScore.test.js asserts parity against the backend.
 */

const LABELS = {
  economic: ['Contracting', 'Soft', 'Mixed', 'Solid', 'Expanding'],
  sentiment: ['Fearful', 'Cautious', 'Neutral', 'Optimistic', 'Exuberant'],
};

/** Band color for a 0-100 score (shared by the panel gauges and the modal). */
export function scoreColor(value) {
  if (value == null) return '#6b7280';
  if (value < 20) return '#dc2626';
  if (value < 40) return '#ea580c';
  if (value < 60) return '#eab308';
  if (value < 80) return '#65a30d';
  return '#16a34a';
}

export function scoreLabel(value, kind = 'economic') {
  if (value == null) return 'No data';
  const bands = LABELS[kind] || LABELS.economic;
  if (value < 20) return bands[0];
  if (value < 40) return bands[1];
  if (value < 60) return bands[2];
  if (value < 80) return bands[3];
  return bands[4];
}

const round = (v, dp) => {
  const f = 10 ** dp;
  return Math.round(v * f) / f;
};

/**
 * Weighted-mean aggregate of per-signal sub-scores.
 *
 * @param {Array<{key,label,category,subscore}>} signals  per-signal sub-scores
 * @param {Object<string,number>} weights                 key -> weight
 * @param {'economic'|'sentiment'} kind                    label band set
 * @returns {{value, label, coverage, degraded, categories, signals}}
 */
export function aggregate(signals, weights, kind = 'economic') {
  let intendedWeight = 0;
  const surviving = [];
  const enriched = [];

  for (const sig of signals || []) {
    const w = Number(weights?.[sig.key]) || 0;
    intendedWeight += Math.max(w, 0);
    const item = { ...sig, weight: w, contribution: null };
    if (sig.subscore != null && w > 0) surviving.push({ sig, w });
    enriched.push(item);
  }

  const totalWeight = surviving.reduce((s, { w }) => s + w, 0);
  if (!surviving.length || totalWeight === 0) {
    return {
      value: null,
      label: scoreLabel(null, kind),
      coverage: 0,
      degraded: true,
      categories: [],
      signals: enriched,
    };
  }

  const value = surviving.reduce((s, { sig, w }) => s + sig.subscore * w, 0) / totalWeight;

  const contribByKey = {};
  for (const { sig, w } of surviving) {
    contribByKey[sig.key] = (sig.subscore * w) / totalWeight;
  }
  for (const item of enriched) {
    if (item.key in contribByKey) item.contribution = round(contribByKey[item.key], 2);
  }

  // Per-category roll-up (weighted mean within each category).
  const cats = {};
  for (const { sig, w } of surviving) {
    (cats[sig.category] ||= []).push({ subscore: sig.subscore, w });
  }
  const categories = Object.entries(cats).map(([name, members]) => {
    const cw = members.reduce((s, m) => s + m.w, 0);
    const csub = cw ? members.reduce((s, m) => s + m.subscore * m.w, 0) / cw : null;
    return {
      name,
      subscore: csub == null ? null : round(csub, 1),
      weight: round(cw, 4),
      signals: (signals || []).filter((s) => s.category === name).map((s) => s.key),
    };
  });
  categories.sort((a, b) => (b.subscore ?? -1) - (a.subscore ?? -1));

  const coverage = intendedWeight ? totalWeight / intendedWeight : 0;
  return {
    value: round(value, 1),
    label: scoreLabel(value, kind),
    coverage: round(coverage, 3),
    degraded: coverage < 0.999,
    categories,
    signals: enriched,
  };
}

/**
 * Flatten the backend `score` block's nested default_weights into the flat
 * {family: {key: weight}} the sliders mutate. Defensive against a missing block.
 */
export function defaultWeightsFrom(scoreBlock) {
  const dw = scoreBlock?.default_weights || {};
  return {
    economic_health: { ...(dw.economic_health || {}) },
    market_sentiment: { ...(dw.market_sentiment || {}) },
  };
}
